/**
 * Draft Storage Utility
 * Uses IndexedDB for short-lived form drafts. Uploaded images are never persisted.
 * Handles availability checks, errors gracefully, and auto-expiration.
 */

const DB_NAME = 'stratform_drafts';
const DB_VERSION = 4;
const STORE_NAME = 'drafts';
const DRAFT_EXPIRY_HOURS = 2;
const MAX_DRAFTS = 20;

let dbInstance = null;
let dbAvailable = null; // null = not checked, true = available, false = unavailable

const isExpired = (draft, now = Date.now()) => (
  !Number.isFinite(draft?.updatedAt)
  || now - draft.updatedAt > DRAFT_EXPIRY_HOURS * 60 * 60 * 1000
);

const removeBinaryValues = (value) => {
  if (typeof Blob !== 'undefined' && value instanceof Blob) return undefined;
  if (typeof value === 'string' && value.startsWith('data:')) return undefined;
  if (Array.isArray(value)) {
    return value.map(removeBinaryValues).filter(item => item !== undefined);
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .map(([key, item]) => [key, removeBinaryValues(item)])
        .filter(([, item]) => item !== undefined)
    );
  }
  return value;
};

const enforceDraftRetention = (db) => new Promise((resolve, reject) => {
  const transaction = db.transaction([STORE_NAME], 'readwrite');
  const store = transaction.objectStore(STORE_NAME);
  const request = store.getAll();

  request.onerror = () => reject(new Error('Failed to inspect drafts'));
  request.onsuccess = () => {
    const now = Date.now();
    const activeDrafts = request.result
      .filter(draft => !isExpired(draft, now))
      .sort((left, right) => right.updatedAt - left.updatedAt);
    const retainedKeys = new Set(activeDrafts.slice(0, MAX_DRAFTS).map(draft => draft.draftKey));
    request.result.forEach(draft => {
      if (!retainedKeys.has(draft.draftKey)) {
        store.delete(draft.draftKey);
      } else {
        // Rewrite retained records so legacy image blobs/data URLs are removed.
        store.put(removeBinaryValues(draft));
      }
    });
  };
  transaction.oncomplete = () => resolve();
  transaction.onerror = () => reject(new Error('Failed to purge expired drafts'));
  transaction.onabort = () => reject(new Error('Failed to purge expired drafts'));
});

/**
 * Check if IndexedDB is available
 */
export const checkIndexedDBAvailable = async () => {
  if (dbAvailable !== null) return dbAvailable;

  try {
    // Test if IndexedDB is accessible (fails in some private browsing modes)
    if (!window.indexedDB) {
      dbAvailable = false;
      return false;
    }

    // Try to open a test database
    const testName = '_idb_test_' + Date.now();
    await new Promise((resolve, reject) => {
      const request = window.indexedDB.open(testName, 1);
      request.onerror = () => reject(new Error('IndexedDB not available'));
      request.onsuccess = () => {
        request.result.close();
        window.indexedDB.deleteDatabase(testName);
        resolve();
      };
    });

    dbAvailable = true;
    return true;
  } catch (e) {
    console.warn('IndexedDB not available:', e);
    dbAvailable = false;
    return false;
  }
};

/**
 * Open or get the IndexedDB database instance
 */
const openDB = async () => {
  if (dbInstance) {
    await enforceDraftRetention(dbInstance);
    return dbInstance;
  }

  const isAvailable = await checkIndexedDBAvailable();
  if (!isAvailable) {
    throw new Error('IndexedDB not available');
  }

  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = async () => {
      dbInstance = request.result;
      try {
        await enforceDraftRetention(dbInstance);
        resolve(dbInstance);
      } catch (error) {
        reject(error);
      }
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create drafts store if not exists
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'draftKey' });
      }

      // Remove image blobs retained by earlier application versions.
      if (db.objectStoreNames.contains('draft_blobs')) {
        db.deleteObjectStore('draft_blobs');
      }
    };
  });
};

/**
 * Generate a unique draft key for a form + user combination
 * @param {string} formId - Form ID
 * @param {string|null} userId - User ID (optional)
 */
export const generateDraftKey = (formId, userId = null) => {
  const userPart = userId || 'anonymous';
  return `draft_${formId}_${userPart}`;
};

/**
 * Generate a unique image ID
 */
export const generateImageId = () => {
  return `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create an image reference object (lightweight, stored in answers)
 */
export const createImageRef = (imageId, mimeType, name, size) => ({
  type: 'image_ref',
  imageId,
  mimeType: mimeType || 'image/jpeg',
  name: name || 'image',
  size: size || 0
});

/**
 * Check if a value is an image reference
 */
export const isImageRef = (value) => {
  return value && typeof value === 'object' && value.type === 'image_ref' && value.imageId;
};

/**
 * Get a draft from IndexedDB
 * @param {string} draftKey - The draft key
 * @returns {Promise<object|null>} - The draft object or null if not found/expired
 */
export const getDraft = async (draftKey) => {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(draftKey);

      request.onerror = () => reject(new Error('Failed to get draft'));
      request.onsuccess = () => {
        const draft = request.result;

        if (!draft) {
          resolve(null);
          return;
        }

        if (isExpired(draft)) {
          deleteDraft(draftKey).catch(() => {});
          resolve(null);
          return;
        }

        resolve(draft);
      };
    });
  } catch (e) {
    console.error('Error getting draft:', e);
    return null;
  }
};

/**
 * Save a draft to IndexedDB (metadata only, no blobs)
 * @param {string} draftKey - The draft key
 * @param {object} data - The draft data (answers with image refs, uiState, etc.)
 */
export const setDraft = async (draftKey, data) => {
  try {
    const db = await openDB();

    const draft = {
      draftKey,
      ...removeBinaryValues(data),
      updatedAt: Date.now()
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(draft);

      request.onerror = () => reject(new Error('Failed to save draft'));
      request.onsuccess = async () => {
        try {
          await enforceDraftRetention(db);
          resolve(true);
        } catch (error) {
          reject(error);
        }
      };
    });
  } catch (e) {
    console.error('Error saving draft:', e);
    throw e;
  }
};

/**
 * Delete a draft from IndexedDB (metadata only)
 * @param {string} draftKey - The draft key
 */
export const deleteDraft = async (draftKey) => {
  try {
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(draftKey);

      request.onerror = () => reject(new Error('Failed to delete draft'));
      request.onsuccess = () => resolve(true);
    });
  } catch (e) {
    console.error('Error deleting draft:', e);
    return false;
  }
};

export const clearAllDrafts = async () => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const request = db.transaction([STORE_NAME], 'readwrite').objectStore(STORE_NAME).clear();
      request.onerror = () => reject(new Error('Failed to clear drafts'));
      request.onsuccess = () => resolve(true);
    });
  } catch (e) {
    console.error('Error clearing drafts:', e);
    return false;
  }
};

/**
 * Convert a blob to base64 data URL
 * @param {Blob} blob - The blob to convert
 * @returns {Promise<string>} - Base64 data URL
 */
export const blobToDataURL = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Convert a base64 data URL to blob
 * @param {string} dataURL - The data URL
 * @returns {Promise<Blob>}
 */
export const dataURLToBlob = async (dataURL) => {
  const response = await fetch(dataURL);
  return response.blob();
};

// ============== SCHEMA & VALIDATION ==============

/**
 * Create a schema hash from form field IDs for validation
 * @param {Array} fields - Form fields array
 * @returns {string} - A simple hash of field IDs
 */
export const createSchemaHash = (fields) => {
  if (!fields || !Array.isArray(fields)) return '';
  const fieldIds = fields.map(f => f.id).sort().join(',');
  let hash = 0;
  for (let i = 0; i < fieldIds.length; i++) {
    const char = fieldIds.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return String(hash);
};

/**
 * Validate and filter restored answers against current schema
 * Only keeps answers for fields that still exist in the form
 * @param {object} savedAnswers - Saved answers from draft
 * @param {Array} currentFields - Current form fields
 * @returns {object} - Filtered answers
 */
export const validateAndFilterAnswers = (savedAnswers, currentFields) => {
  if (!savedAnswers || !currentFields) return {};

  const validFieldIds = new Set(currentFields.map(f => f.id));
  const filtered = {};

  for (const [fieldId, value] of Object.entries(savedAnswers)) {
    if (validFieldIds.has(fieldId)) {
      filtered[fieldId] = value;
    }
  }

  return filtered;
};

/**
 * Validate UI state (currentStep/currentSection) against schema
 * @param {object} uiState - Saved UI state
 * @param {Array} currentFields - Current form fields
 * @param {number} maxSteps - Maximum number of steps (if multi-step)
 * @returns {object} - Validated UI state with safe defaults
 */
export const validateUiState = (uiState, currentFields, maxSteps = 0) => {
  const safeState = {
    currentStep: 0,
    currentSection: null
  };

  if (!uiState) return safeState;

  // Validate currentStep
  if (typeof uiState.currentStep === 'number') {
    if (maxSteps > 0 && uiState.currentStep < maxSteps) {
      safeState.currentStep = uiState.currentStep;
    } else if (maxSteps === 0) {
      // Single-step form, keep at 0
      safeState.currentStep = 0;
    }
  }

  // Validate currentSection (if using sections)
  if (uiState.currentSection && currentFields) {
    // Check if section still exists (could be a field ID or section name)
    const sectionExists = currentFields.some(f =>
      f.id === uiState.currentSection ||
      f.section === uiState.currentSection
    );
    if (sectionExists) {
      safeState.currentSection = uiState.currentSection;
    }
  }

  return safeState;
};

/**
 * Create lightweight metadata for change detection (avoids deep equality on blobs)
 * @param {object} answers - Current answers object
 * @returns {string} - JSON string of metadata for comparison
 */
export const createAnswersMetadata = (answers) => {
  if (!answers) return '';

  const metadata = {};
  for (const [fieldId, value] of Object.entries(answers)) {
    if (isImageRef(value)) {
      // For images, only track the reference metadata
      metadata[fieldId] = { type: 'image_ref', imageId: value.imageId, size: value.size };
    } else if (typeof value === 'object' && value !== null) {
      // For objects (like multi_grid), create a simple hash
      metadata[fieldId] = JSON.stringify(value);
    } else {
      metadata[fieldId] = value;
    }
  }

  return JSON.stringify(metadata);
};

// ============== UI HELPERS ==============

/**
 * Format time ago string for UI display
 * @param {number} timestamp - Unix timestamp in ms
 * @returns {string} - Human readable time ago
 */
export const formatTimeAgo = (timestamp) => {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'hace unos segundos';
  if (diffMins === 1) return 'hace 1 minuto';
  if (diffMins < 60) return `hace ${diffMins} minutos`;
  if (diffHours === 1) return 'hace 1 hora';
  if (diffHours < 24) return `hace ${diffHours} horas`;
  if (diffDays === 1) return 'hace 1 día';
  return `hace ${diffDays} días`;
};

/**
 * Format bytes to human readable string
 * @param {number} bytes
 * @returns {string}
 */
export const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
